import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Output, Input, OnInit, OnDestroy, AfterViewInit, ChangeDetectionStrategy, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, takeUntil, map, Subscription, combineLatest, startWith, BehaviorSubject, filter } from 'rxjs';
import { Task, TaskType } from '@go-modules/interfaces/task';
import { NgxTaskService } from '@go-modules/services/task/task.service';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { Inject } from '@angular/core';
import { AccessibleDropListDirective } from '../../directives/accessible-drop-list/accessible-drop-list.directive';
import { ConfirmDialogComponent, ConfirmDialogData } from '../dialogs/confirm-dialog/confirm-dialog.component';
import { NgxActivityService } from '@go-modules/services/activity/activity.service';
import { TrackByPropPipeModule } from '@go-modules/pipes/track-by-prop/track-by-prop-pipe.module';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { EventService, GoEvent } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';

@Component({
	selector: 'activity-task-pane',
	imports: [
		DragDropModule,
		TranslateModule,
		AccessibleDropListDirective,
		TrackByPropPipeModule,
		MatIconModule,
		MatTooltipModule,
		AsyncPipe
	],
	templateUrl: './activity-task-pane.component.html',
	styleUrls: ['./activity-task-pane.component.scss'],
	changeDetection: ChangeDetectionStrategy.Default
})
export class ActivityTaskPaneComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() public isEditMode = false;
	@Input() public viewOnly = false;
	@Output() public taskSelected = new EventEmitter<void|Task>();
	@ViewChildren('listItem') public listItems: QueryList<ElementRef>;
	public selectedTaskType: TaskType | null = null;
	public activeItem: number;
	public listSubscription: Subscription;
	public originalIndex: { [key: string]: number } = {};
	public filteredTasks$ = new BehaviorSubject<Task[]>([]);
	public visibleFilteredTasks$: Observable<Task[]>;
	public visibleModifiedTasks$: Observable<Task[]>;
	public recommendedTasks$: Observable<Task[]>;
	public modifiedTasks: Task[] = [];
	public hasUnsavedChanges = false;
	private modifiedTasks$ = new BehaviorSubject<Task[]>([]);
	private deletedTasks: Map<TaskType, Task> = new Map();
	private destroy$ = new Subject<void>();
	private refreshRecommended$ = new Subject<void>();
	private excludedTypes$ = new BehaviorSubject<Set<TaskType>>(new Set());

	constructor (
		public activityService: NgxActivityService,
		private taskService: NgxTaskService,
		private dialog: MatDialog,
		private translate: TranslateService,
		private routerService: RouterService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		private eventService: EventService
	) {}

	public ngOnInit (): void {
		const activity = this.selectedService.getActivity();
		const currentParams = this.routerService.getCurrentParams();
		if (currentParams.type) {
			this.selectedTaskType = currentParams.type;
		}

		this.taskService.getTasks(activity.activity_id).pipe(
			takeUntil(this.destroy$)
		).subscribe((tasks) => {
			this.processAndUpdateTasks(tasks, activity);
		});

		this.recommendedTasks$ = this.getRecommendedTasks();

		this.updateOriginalIndex();

		this.visibleFilteredTasks$ = combineLatest([this.filteredTasks$, this.excludedTypes$]).pipe(
			map(([tasks, excluded]) => tasks.filter((t) => !excluded.has(t.type)))
		);

		this.visibleModifiedTasks$ = combineLatest([this.modifiedTasks$, this.excludedTypes$]).pipe(
			map(([tasks, excluded]) => tasks.filter((t) => !excluded.has(t.type)))
		);

		this.eventService.events
			.pipe(
				filter(
					(ev: GoEvent) =>
						[
							EVENT_NAMES.ACTIVITY_EDIT_ACTIVITY_TYPE_CHANGE,
							EVENT_NAMES.ACTIVITY_EDIT_CANCEL_CONFIRMED
						].includes(ev.name as EVENT_NAMES)
				),
				takeUntil(this.destroy$)
			)
			.subscribe((ev: GoEvent) => {
				const excluded = this.getExcludedTypesForActivity(ev);
				this.excludedTypes$.next(excluded);
			});
	}

	private getExcludedTypesForActivity (ev: GoEvent): Set<TaskType> {
		if (ev.name === EVENT_NAMES.ACTIVITY_EDIT_CANCEL_CONFIRMED) {
			return new Set();
		}

		if (!ev.data.has_response_media) {
			return new Set([TaskType.Reflect, TaskType.Submit]);
		}

		return new Set();
	}

	public ngAfterViewInit (): void {
		if (!this.viewOnly) {
			this.taskSelected.emit();
		}

		this.listSubscription = this.listItems.changes.subscribe(() => {
			this.setFocus();
		});
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.listSubscription.unsubscribe();
	}

	public setFocus () {
		if (this.isEditMode) {
			const dragHandle = this.listItems.toArray()[this.activeItem]?.nativeElement.querySelector('.drag-handle');
			dragHandle?.focus();
		} else {
			this.listItems.toArray()[this.activeItem]?.nativeElement.focus();
		}
	}

	public drop (event: CdkDragDrop<Task[]>) {
		if (event.previousContainer !== event.container) {
			if (event.container.id === 'task-list' && event.previousContainer.id === 'recommended-list') {
				this.onAddRecommendedTask(event.item.data, event.currentIndex);
			}
			else if (event.container.id === 'recommended-list' && event.previousContainer.id === 'task-list') {
				this.onTaskRemove(event.item.data);
			}
		} else {
			if (event.container.id === 'task-list') {
				moveItemInArray(this.modifiedTasks, event.previousIndex, event.currentIndex);
				this.activeItem = event.currentIndex;
				this.setFocus();
				this.updateOriginalIndex();
				this.updateTaskSortOrders();
				this.hasUnsavedChanges = true;
			}
		}
	}

	public onActiveItemChange ({currentIndex, newIndex}) {
		moveItemInArray(this.modifiedTasks, currentIndex, newIndex);
		this.activeItem = newIndex;
		this.setFocus();
		this.updateTaskSortOrders();
		this.hasUnsavedChanges = true;
	}

	public onFinalizePosition () {
		this.updateOriginalIndex();
	}

	public updateOriginalIndex () {
		this.modifiedTasks.forEach((task, index) => {
			this.originalIndex[task.id] = index;
		});
	}

	public onCancelReorder ({currentIndex, itemId}) {
		const originalIndex = this.originalIndex[itemId];
		moveItemInArray(this.modifiedTasks, currentIndex, originalIndex);
		this.activeItem = originalIndex;
		this.setFocus();
	}

	public onTaskClick (task: Task): void {
		this.taskSelected.emit(task);

		const activity = this.selectedService.getActivity();
		if (activity) {
			this.selectedTaskType = task.type;
			this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
				activity_id: activity.activity_id,
				type: task.type
			});
		}
	}

	public onTaskRemove (task: Task): void {
		if (task.type === TaskType.Present) {
			return;
		}

		if (this.selectedTaskType === task.type) {
			this.taskSelected.emit();
		}

		const dialogRef: MatDialogRef<ConfirmDialogComponent, any> = this.dialog.open(ConfirmDialogComponent, {
			data: {
				title: this.translate.instant('activity-task-list_remove-task-title'),
				message: this.translate.instant('activity-task-list_remove-task-message'),
				confirmText: this.translate.instant('common_remove'),
				cancelText: this.translate.instant('common_cancel')
			} as ConfirmDialogData
		});

		dialogRef.afterClosed().subscribe((confirm) => {
			if (confirm) {
				const index = this.modifiedTasks.findIndex((t) => t.id === task.id);
				if (index !== -1) {
					this.deletedTasks.set(task.type, task);
					this.modifiedTasks.splice(index, 1);
					this.updateTaskSortOrders();
					this.hasUnsavedChanges = true;
					this.refreshRecommended$.next();
				}
			}
		});
	}

	public onAddRecommendedTask (task: Task, insertAtIndex?: number): void {
		let newTask: Task;
		const activity = this.selectedService.getActivity();

		const deletedTask = this.deletedTasks.get(task.type);
		if (deletedTask) {
			newTask = {
				...deletedTask,
				deleted_at: null,
				deleted_by: null
			};
			this.deletedTasks.delete(task.type);
		} else {
			const minId = Math.min(...this.modifiedTasks.map((t) => t.id), 0);
			newTask = {
				id: minId - 1,
				type: task.type,
				completed: false,
				name: this.activityService.getTaskTitleAndDescription(activity, task).title,
				requirements: this.taskService.getTaskTypeDefaultRequirement(task.type),
				sort_order: 0
			};
		}

		if (insertAtIndex !== undefined && insertAtIndex >= 0 && insertAtIndex <= this.modifiedTasks.length) {
			this.modifiedTasks.splice(insertAtIndex, 0, newTask);
		} else {
			this.modifiedTasks.push(newTask);
		}

		this.updateTaskSortOrders();
		this.hasUnsavedChanges = true;
		this.refreshRecommended$.next();
	}

	public onRecommendedDrop (event: CdkDragDrop<Task[]>): void {
		if (event.previousContainer !== event.container) {
			if (event.container.id === 'recommended-list' && event.previousContainer.id === 'task-list') {
				const task = event.item.data as Task;
				this.onTaskRemove(task);
			}
			else if (event.container.id === 'task-list' && event.previousContainer.id === 'recommended-list') {
				this.onAddRecommendedTask(event.item.data, event.currentIndex);
			}
		}
	}

	private processAndUpdateTasks (tasks: Task[], activity: any): void {
		const deletedTasks = tasks.filter((task) => task.deleted_at);
		const activeTasks = tasks.filter((task) => !task.deleted_at);

		if (!this.hasUnsavedChanges) {
			this.deletedTasks.clear();
			deletedTasks.forEach((task) => {
				this.deletedTasks.set(task.type, {
					...task,
					name: this.activityService.getTaskTitleAndDescription(activity, task).title
				});
			});
		}

		const filteredTasks = activeTasks
			.sort((a, b) => a.sort_order - b.sort_order)
			.map((task) => ({
				...task,
				name: this.activityService.getTaskTitleAndDescription(activity, task).title
			}));

		this.filteredTasks$.next(filteredTasks);

		if (!this.hasUnsavedChanges) {
			this.modifiedTasks = [...filteredTasks];
			this.modifiedTasks$.next([...filteredTasks]);
		}
	}

	private getRecommendedTasks (): Observable<Task[]> {
		const activity = this.selectedService.getActivity();

		return combineLatest([
			this.filteredTasks$,
			this.refreshRecommended$.pipe(startWith(null))
		]).pipe(
			map(([tasks]) => {
				const tasksToCheck = this.isEditMode ? this.modifiedTasks : tasks;
				const existingTypes = new Set(tasksToCheck.map((task) => task.type));
				const defaultTasks = this.taskService.getDefaultTasks(activity)
					.filter((task) => !existingTypes.has(task.type));

				return defaultTasks.map((task) => ({
					...task,
					name: this.activityService.getTaskTitleAndDescription(activity, task).title
				}));
			})
		);
	}

	private updateTaskSortOrders (): void {
		this.modifiedTasks = this.modifiedTasks.map((task, index) => ({
			...task,
			sort_order: index + 1
		}));
		this.modifiedTasks$.next(this.modifiedTasks);
	}

	public getModifiedTasks (): Task[] {
		return this.modifiedTasks;
	}

	public resetModifiedTasks (): void {
		this.hasUnsavedChanges = false;
		this.modifiedTasks = [...this.filteredTasks$.value];
		this.modifiedTasks$.next(this.modifiedTasks);
		this.deletedTasks.clear();
	}
}
